import { useEffect, useRef, useState } from 'react';

/**
 * Scroll-reveal con IntersectionObserver — para las secciones de la landing.
 *
 * Devuelve un `ref` que se cuelga del contenedor a revelar y un booleano `shown`
 * que pasa a `true` una sola vez, cuando el elemento entra al viewport. El estilo
 * lo pone la clase `.reveal` (oculta con fade+rise) + `.reveal-in` (visible),
 * definidas en styles/index.css. Con `prefers-reduced-motion` esas clases se
 * neutralizan por CSS, así que el contenido siempre queda visible.
 *
 * Si IntersectionObserver no existe (SSR / navegadores viejos), revela de una.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(
  options?: IntersectionObserverInit,
) {
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;

    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15, ...options },
    );

    io.observe(el);
    return () => io.disconnect();
  }, [shown]);

  return { ref, shown };
}
